import React, { useEffect, useState } from 'react';
import { Button, Space, Tag, notification, Popconfirm } from 'antd';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { deleteCustomProduct, fetchCustomProducts, refetchCustomProduct, setSelectedCustomProduct } from '../../store/customProductSlice';
import CustomProductDetailDescription from './CustomProductDetailDescription';
import { statusColorGeneration } from '../../constants/statusColors';
import { generateAmazonContentForCustomProduct } from '../../api/generateAmazonContentForCustomProduct';
import { GlobalOutlined, KeyOutlined, LeftOutlined, NumberOutlined, RobotOutlined, RightOutlined, ShopOutlined } from '@ant-design/icons';
import { assignBrandKeywordsToCustomProduct } from '../../api/assignBrandKeywordsToCustomProduct';
import WorkspaceModal from '../Layout/WorkspaceModal';
import './CustomProductDetail.css';

interface CustomProductDetailModalProps {
    open: boolean;
    onClose: () => void;
}

const CustomProductDetailModal: React.FC<CustomProductDetailModalProps> = ({
    open,
    onClose,
}) => {

    const dispatch = useAppDispatch()

    const selectedCompany = useAppSelector((state) => state.companies.selectedCompany)
    const selectedCustomProduct = useAppSelector((state) => state.customProducts.selectedCustomProduct)
    const customProducts = useAppSelector((state) => state.customProducts.customProducts)
    const pagination = useAppSelector((state) => state.customProducts.pagination)
    const activeFilters = useAppSelector((state) => state.customProducts.activeFilters)
    const acticeSorter = useAppSelector((state) => state.customProducts.activeSorter)

    const [currentProductIndex, setCurrentProductIndex] = useState<number>(0)
    const [generating, setGenerating] = useState<boolean>(false)
    const [assigningKeywords, setAssigningKeywords] = useState<boolean>(false)
    const [deleting, setDeleting] = useState<boolean>(false)

    useEffect(() => {
        if (open && selectedCustomProduct) {
            const index = customProducts.findIndex(obj => obj.id === selectedCustomProduct.id);
            setCurrentProductIndex(index);
        }
    }, [open, customProducts, selectedCustomProduct]);

    const handleNext = () => {
        const newIndex = Math.min(currentProductIndex + 1, customProducts.length)
        setCurrentProductIndex(newIndex)
        dispatch(setSelectedCustomProduct(customProducts[newIndex]))

        // Check if you can fetch more products
        if (selectedCompany) {
            if (newIndex === customProducts.length - 2 && pagination.lastVisibleDoc && pagination.hasMore) {
                dispatch(
                    fetchCustomProducts({
                        companyId: selectedCompany?.id,
                        pageSize: pagination.pageSize,
                        lastVisibleDoc: pagination.lastVisibleDoc,
                        activeSorter: acticeSorter,
                        activeFilters: activeFilters,
                        refetch: false
                    })
                );
            }
        }
    }

    const handleLast = () => {
        const newIndex = Math.max(currentProductIndex - 1, 0)
        setCurrentProductIndex(newIndex)
        dispatch(setSelectedCustomProduct(customProducts[newIndex]))
    }

    const handleGenerateContent = async () => {
        if (!selectedCustomProduct || !selectedCompany) {
            console.error("No Product or Company is not selected.");
            return;
        }

        console.log("Generating content for:", selectedCustomProduct?.id);

        try {
            setGenerating(true);
            const result = await generateAmazonContentForCustomProduct(selectedCustomProduct.id, selectedCompany.id)

            if (result.quotaReached) {
                notification.warning({
                    message: "Generation limit reached",
                    description: result.message ?? "You have reached your custom content generation limit. No new content was generated.",
                });
                return;
            }

            dispatch(refetchCustomProduct({
                companyId: selectedCompany.id,
                customProductId: selectedCustomProduct.id
            }));

            notification.success({
                message: "Generation started",
            });

        } catch (error) {
            console.error("Error generating content:", error);

            notification.error({
                message: "Content Generation Failed",
                description: "Custom content generation could not be started.",
            })
        } finally {
            setGenerating(false);
        }
    }

    const handleAssignKeywords = async () => {
        if (!selectedCustomProduct || !selectedCompany) {
            console.error("No Product or Company is not selected.");
            return;
        }

        try {
            setAssigningKeywords(true);
            await assignBrandKeywordsToCustomProduct({
                productId: selectedCustomProduct.id,
                companyId: selectedCompany.id,
            });

            await dispatch(refetchCustomProduct({
                companyId: selectedCompany.id,
                customProductId: selectedCustomProduct.id
            }));

            notification.success({
                message: "Keywords assigned",
            });
        } catch (error) {
            console.error("Error assigning product keywords:", error);

            notification.error({
                message: "Keyword Assignment Failed",
                description: "Run brand research for this product's brand and category first, then try again.",
            })
        } finally {
            setAssigningKeywords(false);
        }
    }

    const handleDeleteProduct = () => {
        if (!selectedCustomProduct || !selectedCompany) {
            console.error("No Product or Company is not selected.");
            return;
        }

        try {
            setDeleting(true);
            dispatch(deleteCustomProduct({ id: selectedCustomProduct.id, companyId: selectedCompany.id }))
            notification.success({
                message: "Product Deleted",
            })

        } catch (error) {
            console.error("Error deleting product:", error);

            notification.error({
                message: "Error deleting product",
            })
        } finally {
            setDeleting(false);
            if (customProducts.length === 1) {
                onClose();
            }
            else if (currentProductIndex === customProducts.length - 1 && customProducts.length > 1) {
                handleLast();
            }
            else {
                handleNext();
            }
        }
    }


    const productName = selectedCustomProduct?.input.productName || 'N/A';
    const shortenedProductName = productName.length > 100 ? `${productName.slice(0, 100)}...` : productName;

    return (
        <WorkspaceModal
            open={open}
            title={
                <div className="custom-product-detail-title">
                    {selectedCustomProduct?.generationStatus && (
                        <Tag color={statusColorGeneration[selectedCustomProduct.generationStatus]}>
                            {selectedCustomProduct.generationStatus.charAt(0).toUpperCase() + selectedCustomProduct.generationStatus.slice(1)}
                        </Tag>
                    )}
                    <span className="custom-product-detail-title-main">
                        {shortenedProductName}
                    </span>
                    {selectedCustomProduct && (
                        <span className="custom-product-detail-title-meta">
                            {selectedCustomProduct.input.brand && (
                                <span className="custom-product-detail-title-meta-item">
                                    <ShopOutlined />
                                    {selectedCustomProduct.input.brand}
                                </span>
                            )}
                            {selectedCustomProduct.input.marketplace && (
                                <span className="custom-product-detail-title-meta-item">
                                    <GlobalOutlined />
                                    {selectedCustomProduct.input.marketplace}
                                </span>
                            )}
                            <span className="custom-product-detail-title-meta-item">
                                <NumberOutlined />
                                ID: {selectedCustomProduct.id}
                            </span>
                        </span>
                    )}
                </div>
            }
            className="custom-product-detail-modal"
            onClose={onClose}
            footer={(
                <div className="custom-product-detail-footer">
                    <Popconfirm
                        title="Delete Product"
                        description="Are you sure you want to delete this product?"
                        onConfirm={handleDeleteProduct}
                        okType='danger'
                        okText="Delete"
                        cancelText="No"
                    >
                        <Button danger type="text" loading={deleting} disabled={deleting}>Delete</Button>
                    </Popconfirm>
                    <div className="custom-product-detail-footer-actions">
                        <Button key="analyze" type="default" disabled={generating} loading={generating} onClick={handleGenerateContent} icon={<RobotOutlined />}>
                            {generating ? "Generating" : "Generate"}
                        </Button>
                        <Button key="assign-keywords" type="default" disabled={assigningKeywords || generating} loading={assigningKeywords} onClick={handleAssignKeywords} icon={<KeyOutlined />}>
                            {assigningKeywords ? "Assigning" : "Assign Keywords"}
                        </Button>
                        <Space.Compact className="custom-product-detail-pagination-actions">
                            <Button
                                aria-label="Previous product"
                                disabled={currentProductIndex === 0}
                                icon={<LeftOutlined />}
                                key="last"
                                type="default"
                                onClick={handleLast}
                            />
                            <Button
                                aria-label="Next product"
                                disabled={currentProductIndex === customProducts.length - 1}
                                icon={<RightOutlined />}
                                key="next"
                                type="default"
                                onClick={handleNext}
                            />
                        </Space.Compact>
                        <Button key="close" type="primary" onClick={onClose}>
                            Close
                        </Button>
                    </div>
                </div>
            )}
        >
            <div className="custom-product-detail-body">
                <CustomProductDetailDescription product={selectedCustomProduct} />
            </div>
        </WorkspaceModal>
    );
};

export default CustomProductDetailModal;
